import { Tooltip } from '@getro/rombo';
import copy from 'copy-to-clipboard';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Link, X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useParams, Switch, Route, useHistory, useRouteMatch, NavLink } from 'react-router-dom';
import toast from 'react-hot-toast';

import { matchesApi } from 'services/matches';
import { trackEvent } from '../../../helpers/analytics';
import { ContactMoreActions } from '../contactActionPanel/contactMoreActions';
import { Tabs } from '../../atoms/tabs';
import { ContactInfo } from './contactInfo';
import { ContactDetailPortal } from './contactDetailPortal';
import { useGetContactDetailContactById } from './hooks/useGetContactDetailContactById';
import { ContactDetailLoader } from './contactDetailLoader';
import { Experience } from './components/experience';
import { JobSeeking } from './components/jobSeeking';
import { Notes } from './components/notes';
import { ConnectionPaths } from '../connectionPaths';
import CompanyDetail from './components/companyDetail';

export const ContactDetail = ({
  queryParams,
  contact: contactProp,
  type,
  match,
  matchListInfo,
  isTalentNetworkEnabled,
  list,
  onClickHighlight,
  onClose,
  loading,
  deleteContactFromList,
  fullContactView,
  addedToSharedList,
  page,
  showMoreOptions,
}) => {
  const { contactId } = useParams();
  const { contact, isFetching, network } = useGetContactDetailContactById({
    type,
    contactId: contactId ? parseInt(contactId, 10) : contactProp.id,
  });
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const dispatch = useDispatch();

  const showJobSeeking = type !== 'public' && (isTalentNetworkEnabled || network?.features?.includes('job_board'));
  const showCompanyDetail = network?.features?.includes('contact_companies_tab') ?? true;
  const showConnectionPaths = type !== 'public';
  const tabs = [
    {
      id: 'tab-contact-full-profile-experience',
      title: `Experience`,
      to: `${url}${history.location.search}`,
      show: true,
      tabName: 'experience',
    },
    ...(showCompanyDetail
      ? [
          {
            id: 'tab-contact-full-profile-company-details',
            title: 'Company details',
            to: `${url}/company-details${history.location.search}`,
            show: true,
          },
        ]
      : []),
    {
      id: 'tab-contact-full-profile-job-seeking',
      title: 'Job-seeking',
      to: `${url}/job-seeking${history.location.search}`,
      show: showJobSeeking,
      tabName: 'job_seeking',
    },
    {
      id: 'tab-contact-full-profile-connections',
      title: 'Connection paths',
      to: `${url}/connections${history.location.search}`,
      show: showConnectionPaths,
      tabName: 'connection_paths',
    },

    {
      id: 'tab-contact-full-profile-notes',
      title:
        contact?.notes?.length > 0 ? (
          <>
            Notes
            <Text
              as="span"
              fontSize="12px"
              fontWeight="medium"
              ml="8px"
              sx={{ border: '1px solid', borderColor: 'neutral.400', borderRadius: 'default', px: '4px' }}
            >
              {contact?.notes?.length}
            </Text>
          </>
        ) : (
          'Notes'
        ),
      to: `${url}/notes${history.location.search}`,
      show: true,
      tabName: 'notes',
    },
  ];

  const onCloseView = useCallback(
    (triggerClose = true) => {
      document.getElementsByTagName('body')[0].style.overflowY = '';

      if (triggerClose) {
        onClose();
      }

      if (match.deleted) {
        dispatch(
          matchesApi.util.updateQueryData(
            'getMatchListById',
            { matchListId: `${matchListInfo.id}` },
            ({ matchList }) => {
              Object.assign(matchList, {
                matches: matchList.matches.filter((item) => item.id !== match.id),
              });
            },
          ),
        );
      }
    },
    [onClose, match.deleted, match.id, matchListInfo.id, dispatch],
  );

  const trackPageEvent = useCallback(
    (event, additionalProperties = {}) => {
      if (contact) {
        trackEvent(event, {
          collectionId: network?.id,
          contactId: contact.id,
          page,
          ...additionalProperties,
        });
      }
    },
    [contact, network?.id, page],
  );

  const handleTabClick = useCallback(
    (tabName) => {
      trackPageEvent('contacts:contact_full_profile:change_tab', { tabName });
    },
    [trackPageEvent],
  );

  useEffect(() => {
    if (contact?.id >= 0) {
      trackPageEvent('contacts:open_contact_full_profile');
    }
  }, [contact?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.getElementsByTagName('body')[0].style.overflowY = 'hidden';

    return () => {
      onCloseView(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onCloseView();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onCloseView]);

  if (loading || isFetching || !contact) {
    return (
      <ContactDetailPortal onCloseView={onCloseView}>
        <ContactDetailLoader onCloseView={onCloseView} />
      </ContactDetailPortal>
    );
  }

  return (
    <ContactDetailPortal onCloseView={onCloseView}>
      <Flex
        as="header"
        alignItems="center"
        justifyContent="space-between"
        height="40px"
        px="4px"
        sx={{ borderBottom: '1px solid', borderColor: 'border.subtle' }}
      >
        <Flex
          data-testid="close-button"
          alignItems="center"
          justifyContent="center"
          width="40px"
          height="40px"
          color="neutral.400"
          sx={{
            cursor: 'pointer',
          }}
          onClick={onCloseView}
        >
          <Box as={X} width="16px" height="16px" aria-hidden="true" />
        </Flex>
        <Flex alignItems="center" justifyContent="center" height="100%">
          <Tooltip
            trigger={
              <Flex
                height="32px"
                width="32px"
                alignItems="center"
                justifyContent="center"
                sx={{ cursor: 'pointer', color: 'neutral.400' }}
                onClick={() => {
                  const newUrl = new URL(window.location.href);
                  const fullUrl = `${newUrl.origin}${newUrl.pathname}${newUrl.search}${newUrl.hash}`;

                  copy(fullUrl);
                  toast('Link copied!', { icon: 'success', id: 'copy-contact-link' });
                }}
              >
                <Box as={Link} width="16px" height="16px" aria-hidden="true" />
              </Flex>
            }
            content="Copy link to contact profile"
          />
          {showMoreOptions && (
            <ContactMoreActions
              page={page}
              queryParams={queryParams}
              contact={contact}
              sx={{
                height: '32px',
                width: '32px',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.main',
              }}
              deleteContactFromList={deleteContactFromList}
              fullContactView={fullContactView}
              list={list}
              showMoreOptions
            />
          )}
        </Flex>
      </Flex>
      <Flex flex="1" flexDirection={['column', 'column', 'row']}>
        <Box
          p="16px"
          width={['100%', '100%', '320px']}
          flex="0 0 auto"
          sx={{ borderRight: '1px solid', borderColor: 'border.subtle' }}
        >
          <ContactInfo
            queryParams={queryParams}
            contact={contact}
            type={type}
            match={match}
            matchListInfo={matchListInfo}
            scoreDetails={match.scoreDetails}
            list={list}
            onClickHighlight={onClickHighlight}
            deleteContactFromList={deleteContactFromList}
            fullContactView={fullContactView}
            addedToSharedList={addedToSharedList}
            page={page}
            trackPageEvent={trackPageEvent}
            networkId={network?.id}
            showJobSeeking={showJobSeeking}
          />
        </Box>
        <Box flexGrow="1" pt="16px" sx={{ '& > nav > ul > li:first-child': { ml: '16px' } }}>
          <Tabs
            tabs={tabs
              .filter(({ show }) => show)
              .map(({ id, title, to, tabName }) => (
                <Box as="li" key={id}>
                  <Flex
                    data-testid={id}
                    as={NavLink}
                    to={to}
                    exact
                    onClick={() => handleTabClick(tabName)}
                    sx={{
                      textDecoration: 'none',
                      alignItems: 'flex-start',
                      fontSize: '14px',
                      lineHeight: '16px',
                      pb: '12px',
                      height: '100%',
                    }}
                  >
                    <Text as="span">{title}</Text>
                  </Flex>
                </Box>
              ))}
          />
          <Box p="16px">
            <Switch>
              <Route exact path={`${path}`}>
                <Experience
                  pastPositions={contact.contactWorkExperiences}
                  education={contact.contactEducations}
                  skills={contact.skills}
                  contact={contact}
                  networkId={network?.id}
                  list={list}
                  type={type}
                />
              </Route>

              {showCompanyDetail && (
                <Route path={`${path}/company-details`}>
                  <CompanyDetail contact={contact} type={type} />
                </Route>
              )}
              {showJobSeeking && (
                <Route path={`${path}/job-seeking`}>
                  <JobSeeking
                    signupInfo={contact.signupInfo}
                    jobApplications={contact.jobApplications}
                    contactId={contact.id}
                  />
                </Route>
              )}
              {showConnectionPaths && (
                <Route path={`${path}/connections`}>
                  <ConnectionPaths network={network} contact={contact} />
                </Route>
              )}

              <Route path={`${path}/notes`}>
                <Notes queryParams={queryParams} items={contact.notes} contactId={contact.id} type={type} />
              </Route>
            </Switch>
          </Box>
        </Box>
      </Flex>
    </ContactDetailPortal>
  );
};

ContactDetail.propTypes = {
  queryParams: PropTypes.object,
  contact: PropTypes.object,
  type: PropTypes.oneOf(['default', 'read', 'matches', 'public']),
  match: PropTypes.object,
  matchListInfo: PropTypes.object,
  isTalentNetworkEnabled: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onClickHighlight: PropTypes.func,
  loading: PropTypes.bool,
  list: PropTypes.object,
  deleteContactFromList: PropTypes.bool,
  fullContactView: PropTypes.bool,
  addedToSharedList: PropTypes.bool,
  page: PropTypes.string.isRequired,
  showMoreOptions: PropTypes.bool,
};

ContactDetail.defaultProps = {
  queryParams: {},
  contact: {},
  type: 'default',
  match: {},
  matchListInfo: {},
  isTalentNetworkEnabled: false,
  onClickHighlight: null,
  loading: false,
  list: null,
  deleteContactFromList: false,
  fullContactView: false,
  addedToSharedList: false,
  showMoreOptions: true,
};
